import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, X } from "lucide-react";

/**
 * Free-launch announcement popup for the public landing page.
 *
 * Fetches the promo state from /api/subscription/promo and, when active,
 * shows a centered card after a short delay. Dismissal is persisted in
 * localStorage so the user only sees it once per browser (per major promo).
 */
const DISMISS_KEY = "ydd_free_promo_dismissed_v1";

export default function FreeAccessPopup() {
  const [promo, setPromo] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;

    // Skip immediately if already dismissed in this browser.
    try {
      if (typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1") {
        return undefined;
      }
    } catch (err) {
      console.debug("FreeAccessPopup: localStorage read failed (private browsing?)", err);
    }

    (async () => {
      try {
        const { data } = await api.get("/subscription/promo");
        if (cancelled) return;
        if (!data?.free_pass_active) return;
        setPromo(data);
        // small delay so the popup doesn't fight the hero animation for attention
        timer = window.setTimeout(() => setOpen(true), 900);
      } catch (err) {
        // Public endpoint — if it fails, just don't show the popup.
        console.debug("FreeAccessPopup: promo probe failed", err);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); }
    catch (err) { console.debug("FreeAccessPopup: localStorage write failed", err); }
  };

  if (!open || !promo) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
      data-testid="free-promo-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="free-promo-headline"
      onClick={dismiss}
    >
      <div
        className="absolute inset-0 bg-foreground/45 backdrop-blur-sm"
        data-testid="free-promo-backdrop"
      />
      <div
        className="relative w-full max-w-md bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute right-3 top-3 h-8 w-8 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center"
          aria-label="Dismiss announcement"
          data-testid="free-promo-close"
        >
          <X size={16} />
        </button>

        <div className="px-7 pt-8 pb-3 text-center">
          <div className="mx-auto h-12 w-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <Sparkles size={22} />
          </div>
          <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
            Launch promo
          </p>
          <h2
            id="free-promo-headline"
            className="mt-2 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground"
            data-testid="free-promo-headline"
          >
            {promo.headline || "Everything is free until January 2027"}
          </h2>
          <p className="mt-3 text-sm text-foreground/80 leading-relaxed" data-testid="free-promo-subheadline">
            {promo.subheadline}
          </p>
        </div>

        <div className="px-7 pb-7 pt-2 flex flex-col gap-2">
          <Link
            to="/register"
            onClick={dismiss}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2.5 text-sm font-medium text-center transition-colors"
            data-testid="free-promo-cta"
          >
            Create a free account →
          </Link>
          <button
            onClick={dismiss}
            className="w-full text-xs text-muted-foreground hover:text-foreground py-1.5"
            data-testid="free-promo-later"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
